import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/i18n";
import { usePageMeta, initials } from "@/lib/format";
import { PageHeader } from "@/components/StatCard";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { ChevronLeft, Send, Gem, Mail } from "lucide-react";

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-700",
  accepted: "bg-emerald-100 text-emerald-700",
  revoked: "bg-secondary text-muted-foreground",
};

function Stat({ label, value, testId }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-3 text-center" data-testid={testId}>
      <p className="font-display text-xl font-semibold text-foreground">{value}</p>
      <p className="text-[11px] text-muted-foreground">{label}</p>
    </div>
  );
}

export default function InnerCircleInvites() {
  const { user } = useAuth();
  const t = useT();
  usePageMeta(t("ici.meta_title"));
  const [rows, setRows] = useState(null);
  const [stats, setStats] = useState(null);
  const [emails, setEmails] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/coach/inner-circle/invitations"); setRows(data?.invitations || []); } catch { setRows([]); }
    try { const { data } = await api.get("/coach/inner-circle/invite-analytics"); setStats(data); } catch { setStats(null); }
  }, []);
  useEffect(() => { if (user?.role === "coach") load(); }, [user, load]);

  if (user && user.role !== "coach") return <Navigate to="/app/circles" replace />;

  const send = async () => {
    const list = emails.split(/[\s,;]+/).map((e) => e.trim()).filter((e) => /@/.test(e));
    if (list.length === 0) { toast.error(t("ici.err_email")); return; }
    setSending(true);
    try {
      const { data } = await api.post("/coach/inner-circle/invite", { emails: list, note: note.trim() || null });
      toast.success(t("ici.sent", { n: data?.invitations_created ?? list.length }));
      setEmails(""); setNote("");
      await load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="coach-ic-invites-page">
      <Seo title={t("ici.seo")} noindex />
      <Link to="/app/coach/circles/inner" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="ici-back">
        <ChevronLeft className="h-4 w-4" /> {t("ici.back")}
      </Link>
      <PageHeader eyebrow={t("ici.eyebrow")} title={t("ici.title")} subtitle={t("ici.subtitle")} />

      {stats && (
        <div className="mb-5 grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="ici-stats">
          <Stat label={t("ici.stat_sent")} value={stats.sent ?? 0} testId="ici-stat-sent" />
          <Stat label={t("ici.stat_accept_rate")} value={`${stats.accept_rate ?? 0}%`} testId="ici-stat-rate" />
          <Stat label={t("ici.stat_active")} value={stats.active_subscribers ?? 0} testId="ici-stat-active" />
          <Stat label={t("ici.stat_retention")} value={stats.retention_30d ?? 0} testId="ici-stat-retention" />
        </div>
      )}

      <div className="mb-6 rounded-2xl border border-border bg-card p-4">
        <p className="mb-2 flex items-center gap-1.5 font-display text-lg text-foreground"><Mail className="h-4 w-4 text-primary" /> {t("ici.invite_title")}</p>
        <Textarea className="min-h-[70px]" value={emails} onChange={(e) => setEmails(e.target.value)} placeholder={t("ici.emails_ph")} data-testid="ici-emails" />
        <Textarea className="mt-2 min-h-[52px]" value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("ici.note_ph")} maxLength={200} data-testid="ici-note" />
        <div className="mt-2 flex justify-end">
          <Button className="gap-1.5 rounded-full" onClick={send} disabled={sending} data-testid="ici-send"><Send className="h-4 w-4" /> {sending ? t("common.saving") : t("ici.send")}</Button>
        </div>
      </div>

      <p className="mb-2 font-display text-lg text-foreground">{t("ici.list_title")}</p>
      {rows === null ? (
        <div className="space-y-2">{[0, 1].map((i) => <Skeleton key={i} className="h-16 rounded-2xl" />)}</div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center" data-testid="ici-empty">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground"><Gem className="h-6 w-6" /></div>
          <p className="mt-4 text-sm text-muted-foreground">{t("ici.empty")}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((inv) => (
            <div key={inv.id} data-testid={`ici-inv-${inv.id}`} className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3">
              {inv.client_avatar_url ? <img src={inv.client_avatar_url} alt="" className="h-9 w-9 rounded-full object-cover" /> : <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/15 text-xs font-semibold text-primary">{initials(inv.client_name || inv.email || "?")}</span>}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-foreground">{inv.client_name || inv.email}</p>
                {inv.note && <p className="truncate text-xs text-muted-foreground">{inv.note}</p>}
              </div>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${STATUS_COLORS[inv.status] || STATUS_COLORS.pending}`}>{t(`ici.status_${inv.status}`)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
